import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { http, errMsg, formatARS } from "@/lib/api";
import { Card } from "@/components/checkout/Field";
import { CategoryRow } from "./CategoryRow";
import { ProductDialog } from "./ProductDialog";

export const AdminMenu = () => {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [dialog, setDialog] = useState({ open: false, product: null, cat: "" });
  const qc = useQueryClient();

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([http.get("/admin/categories"), http.get("/admin/products")]);
      setCats(c.data);
      setProds(p.data);
      qc.invalidateQueries({ queryKey: ["menu"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [qc]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addCat = () => newCat.trim() && run(() => http.post("/admin/categories", { name: newCat.trim(), active: true, has_extras: false, half_and_half: false, has_doughs: false, extras: [], order: cats.length }), "Categoría creada").then(() => setNewCat(""));
  const updCat = (cat, body) => run(() => http.put(`/admin/categories/${cat.id}`, body));
  const delCat = (cat) => window.confirm(`¿Eliminar "${cat.name}" y todos sus productos?`) && run(() => http.delete(`/admin/categories/${cat.id}`), "Categoría eliminada");
  const delProd = (p) => window.confirm(`¿Eliminar "${p.name}"?`) && run(() => http.delete(`/admin/products/${p.id}`), "Producto eliminado");
  const toggleStock = async (p) => {
    setProds((list) => list.map((x) => (x.id === p.id ? { ...x, available: !x.available } : x)));
    run(() => http.patch(`/admin/products/${p.id}/stock`), p.available ? `${p.name}: Agotado` : `${p.name}: Disponible`);
  };

  return (
    <div className="space-y-4 pb-10" data-testid="admin-menu">
      <Card title="Categorías">
        <p className="text-xs text-[#A89F91]">Activá o desactivá categorías con el interruptor. El botón <b className="text-green-400">Extras</b> habilita la personalización con agregados para esa categoría.</p>
        <div className="space-y-2">{cats.map((c) => <CategoryRow key={c.id} cat={c} onUpdate={(b) => updCat(c, b)} onDelete={delCat} />)}</div>
        <div className="flex gap-2">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nueva categoría" className="field-dark flex-1 min-w-0 rounded-xl px-4 py-2.5 text-sm" data-testid="new-category-input" />
          <button onClick={addCat} className="px-4 rounded-xl bg-amber-500 text-[#120E0E] font-bold text-sm" data-testid="new-category-add-btn"><Plus className="w-4 h-4" /></button>
        </div>
      </Card>

      {cats.map((c) => (
        <Card key={c.id} title={c.name}>
          <div className="space-y-2">
            {prods.filter((p) => p.category_id === c.id).map((p) => (
              <div key={p.id} className={`flex items-center gap-3 p-2.5 rounded-xl bg-[#FFFDF9] text-[#1F1917] ${p.available ? "" : "opacity-70"}`} data-testid={`admin-product-row-${p.id}`}>
                <div className="w-12 h-12 rounded-lg bg-[#F7F2EA] overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className={`w-full h-full object-cover ${p.available ? "" : "grayscale"}`} />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{p.name}</p>
                  <p className="text-xs text-[#B91C1C] font-semibold">{formatARS(p.price)}</p>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <Switch checked={p.available} onCheckedChange={() => toggleStock(p)} data-testid={`stock-switch-${p.id}`} className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-400" />
                  <span className={`text-[9px] font-bold uppercase ${p.available ? "text-green-700" : "text-gray-500"}`} data-testid={`stock-label-${p.id}`}>{p.available ? "Disponible" : "Agotado"}</span>
                </div>
                <button onClick={() => setDialog({ open: true, product: p, cat: c.id })} className="p-1.5 text-[#645A53]" data-testid={`product-edit-btn-${p.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delProd(p)} className="p-1.5 text-[#645A53] hover:text-red-600" data-testid={`product-delete-btn-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setDialog({ open: true, product: null, cat: c.id })} className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300" data-testid={`add-product-btn-${c.id}`}>
            <Plus className="w-4 h-4" /> Agregar producto
          </button>
        </Card>
      ))}
      <ProductDialog open={dialog.open} product={dialog.product} defaultCategory={dialog.cat} categories={cats} onClose={() => setDialog({ open: false, product: null, cat: "" })} onSaved={load} />
    </div>
  );
};
